/**
 * Brand-accurate payment icons for Bangladeshi e-wallets.
 * Uses the real official logo files supplied by the operator
 * (saved in /public/payment-logos/).
 */
import React from "react";

const tile =
  "h-11 w-11 sm:h-14 sm:w-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 overflow-hidden relative";

const imgClass = "h-full w-full object-contain";

/* bKash — pink brand background to make the logo pop */
export const BkashIcon = () => (
  <div
    className={tile}
    style={{
      background: "linear-gradient(135deg,#E2136E 0%,#B40E58 100%)",
      boxShadow:
        "0 12px 26px -12px rgba(226,19,110,0.7), inset 0 1px 0 rgba(255,255,255,0.18)",
      padding: "8px",
    }}
    data-testid="payment-icon-bkash"
  >
    <img
      src="/payment-logos/bkash.png"
      alt="bKash"
      className={imgClass}
      draggable={false}
    />
  </div>
);

/* Nagad — white tile so the orange/red brand mark is fully readable */
export const NagadIcon = () => (
  <div
    className={tile}
    style={{
      background: "#ffffff",
      boxShadow:
        "0 12px 26px -12px rgba(243,98,32,0.45), inset 0 1px 0 rgba(255,255,255,0.7)",
      border: "1px solid rgba(0,0,0,0.06)",
      padding: "6px",
    }}
    data-testid="payment-icon-nagad"
  >
    <img
      src="/payment-logos/nagad.png"
      alt="Nagad"
      className={imgClass}
      draggable={false}
    />
  </div>
);

/* Rocket — purple brand background matches the official Rocket card */
export const RocketIcon = () => (
  <div
    className={tile}
    style={{
      background: "#8E1B8F",
      boxShadow:
        "0 12px 26px -12px rgba(142,27,143,0.7), inset 0 1px 0 rgba(255,255,255,0.18)",
      padding: "4px",
    }}
    data-testid="payment-icon-rocket"
  >
    <img
      src="/payment-logos/rocket.png"
      alt="Rocket"
      className={imgClass}
      draggable={false}
    />
  </div>
);

/* UPay — light tile keeps the brand's signature look */
export const UPayIcon = () => (
  <div
    className={tile}
    style={{
      background: "#F2F2F2",
      boxShadow:
        "0 12px 26px -12px rgba(31,136,255,0.45), inset 0 1px 0 rgba(255,255,255,0.7)",
      border: "1px solid rgba(0,0,0,0.06)",
      padding: "4px",
    }}
    data-testid="payment-icon-upay"
  >
    <img
      src="/payment-logos/upay.png"
      alt="UPay"
      className={imgClass}
      draggable={false}
    />
  </div>
);

export const PaymentIcon = ({ id }) => {
  switch (id) {
    case "bkash":
      return <BkashIcon />;
    case "nagad":
      return <NagadIcon />;
    case "rocket":
      return <RocketIcon />;
    case "upay":
      return <UPayIcon />;
    case "usdt_trc20":
      return <CryptoTile logo="/payment-logos/usdt.svg" bg="#26a17b" network="TRC20" netColor="#ef0027" />;
    case "usdt_bep20":
      return <CryptoTile logo="/payment-logos/usdt.svg" bg="#26a17b" network="BEP20" netColor="#f3ba2f" netTextColor="#1a1208" />;
    case "usdt_erc20":
      return <CryptoTile logo="/payment-logos/usdt.svg" bg="#26a17b" network="ERC20" netColor="#627eea" />;
    case "usdc_polygon":
      return <CryptoTile logo="/payment-logos/usdc.svg" bg="#2775ca" network="MATIC" netColor="#8247e5" />;
    case "trx":
      return <CryptoTile logo="/payment-logos/trx.svg"  bg="#ef0027" network="TRC20" netColor="#ffffff" netTextColor="#ef0027" />;
    case "bnb":
      return <CryptoTile logo="/payment-logos/bnb.svg"  bg="#f3ba2f" network="BEP20" netColor="#212121" />;
    case "eth":
      return <CryptoTile logo="/payment-logos/eth.svg"  bg="#627eea" network="ERC20" netColor="#3c3c3d" />;
    case "ton":
      return <CryptoTile logo="/payment-logos/ton.svg"  bg="#0098ea" network="TON"   netColor="#0098ea" />;
    case "btc":
      return <CryptoTile logo="/payment-logos/btc.svg"  bg="#f7931a" network="BTC"   netColor="#f7931a" />;
    default:
      return <div className={tile + " bg-white/5"} />;
  }
};

function CryptoTile({ logo, bg, network, netColor = "#1f2937", netTextColor = "#ffffff" }) {
  return (
    <div
      className={tile}
      style={{
        background: `linear-gradient(135deg, ${bg} 0%, ${bg}cc 100%)`,
        boxShadow: `0 12px 26px -12px ${bg}99, inset 0 1px 0 rgba(255,255,255,0.18)`,
        padding: "8px",
      }}
    >
      <img src={logo} alt="" className="h-full w-full object-contain drop-shadow" draggable={false} />
      {network && (
        <span
          className="absolute bottom-1 right-1 px-1.5 py-[1px] rounded text-[8px] font-extrabold uppercase tracking-tight"
          style={{ background: netColor, color: netTextColor, lineHeight: "10px" }}
        >
          {network}
        </span>
      )}
    </div>
  );
}
